import React, {
  Component,
  Children,
  cloneElement,
  ReactElement,
  HTMLAttributes,
} from "react";
import { RadioButtonProps } from "./RadioButton";
import { InputError } from "../InputError/InputError";
import { FormFieldProps, FormField } from "../../commonTypes/formField.types";
import { hashCode } from "../../utils/hash";

export type RadioButtonGroupProps<T> = FormFieldProps<T>;
export class RadioButtonGroup<TValue> extends Component<
  RadioButtonGroupProps<TValue>,
  FormField<TValue>
> {
  static defaultProps = {
    fieldName: "",
    value: "",
    isRequired: false,
    isTouched: false,
    errorMessage: "",
    onChange: () => {
      /* noop */
    },
    onBlur: () => {},
  } as Partial<RadioButtonGroupProps<any>>;

  private _id: string;
  private _errorId: string;
  private _latestValue: TValue | undefined;

  constructor(props: any) {
    super(props);

    this._id = this.props.id || hashCode(this.props.fieldName, "radiobtnGroup");
    this._errorId = hashCode(props.id || props.fieldName, "error");

    const { fieldName, value, isRequired, label } = this.props;

    if (props.value !== undefined) {
      this._latestValue = value;
    } else {
      this._latestValue = undefined;
    }

    this.state = {
      value: this._latestValue,
      fieldName,
      isRequired,
      label,
      isTouched: false,
      errorMessage: "",
    };
  }

  private get _ariaAttributes(): HTMLAttributes<any> {
    const { errorAriaAttributes } = this.props;
    if (errorAriaAttributes) return errorAriaAttributes;
    if (this._errorMessage)
      return { "aria-invalid": true, "aria-describedby": this._errorId };
    return null;
  }

  render() {
    const {
      children,
      fieldName,
      isRequired,
      checkboxWithProps,
      label,
      ariaLabel,
      subLabel,
    } = this.props;
    const { value } = this.state;
    const errorMessage = this._errorMessage;

    const childrenWithProps = Children.map(
      children,
      (child: ReactElement<RadioButtonProps<any>>) => {
        const compareValue = checkboxWithProps ? this.props.value : value;
        const childProps: RadioButtonProps<any> = {
          onChange: this.onChangeValue,
          checked: child.props.value === compareValue,
          fieldName,
          onBlur: this.onHandleBlur,
          isRequired,
          errorAriaAttributes: this._ariaAttributes,
        };
        return cloneElement(child, childProps);
      }
    );

    return (
      <>
        {ariaLabel && label && (
          <div role="radiogroup" aria-labelledby={ariaLabel}>
            <div className="like-a-label" id={ariaLabel}>
              {label}{" "}
              {subLabel && (
                <div className="like-a-label__sub-label">{subLabel}</div>
              )}
            </div>
            {childrenWithProps}
            {errorMessage && (
              <InputError id={this._errorId} errorText={errorMessage} />
            )}
          </div>
        )}
        {(!ariaLabel || !label) && (
          <div>
            {childrenWithProps}
            {errorMessage && (
              <InputError id={this._errorId} errorText={errorMessage} />
            )}
          </div>
        )}
      </>
    );
  }

  private get _errorMessage(): string | undefined {
    let { errorMessage } = this.state;
    if (!errorMessage && this.props.errorMessage) {
      errorMessage = this.props.errorMessage;
    }

    return errorMessage;
  }

  private onHandleBlur = (e: React.FocusEvent) => {
    if (!this.state.isTouched) {
      this.setState(
        {
          isTouched: true,
        },
        () => {
          this.props.onBlur(e);
        }
      );
    }
  };

  private onChangeValue = (field: FormField<TValue>) => {
    this.setState(
      {
        value: field.value,
      },
      () => {
        this.props.onChange(this.state);
      }
    );
  };
}
